import { isValid } from 'date-fns';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TogglePill } from '@/common/components';
import { DateRangeDropdown, DateRangeSelection } from '@builder';
import { COMMON_TRANSLATION, LAYOUT_THREE } from '@/common/constants';
import { getDateWithoutTZ, getFirstDayOfMonth, getLastDayOfMonth, getSelectedDateRangeString } from '@/common/utils';
import useConsentedParticipantsStore from '@/modules/my-account/store/useConsentedParticipantsStore/useConsentedParticipantsStore';
import { STUDY_INSIGHTS_TRANSLATION } from '@/modules/study-insights/constants';
import { useSequenceSamplesStore } from '@/modules/study-insights/store';
import {
  StyledContainer,
  StyledDate,
  StyledDateContainer,
  StyledSelectContainer,
  StyledSelectedRange,
} from './ChartPillAndDateFilter.styles';
import { PillIds } from '../types/PillIds';
import { ChartPillAndDateFilterProps } from './ChartPillAndDateFilter.types';

const ChartPillAndDateFilter: FC<ChartPillAndDateFilterProps> = ({
  onChangeDate,
  onChangePill,
  pillOptions,
  selectedPill,
}) => {
  const { selectedDateRange, setSelectedDateRange } = useSequenceSamplesStore();
  const [startDate, setStartDate] = useState<Date | undefined>(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState<Date | undefined>(getLastDayOfMonth());
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);

  const { endDate: storeEndDate, startDate: storeStartDate } = useConsentedParticipantsStore();

  const handlePillClick = (id: string): void => {
    onChangePill(id as PillIds);
  };

  const getSelectedData = (): string => {
    if (!startDate || !isValid(startDate) || !endDate || !isValid(endDate)) {
      return t('date_range_dropdown_options.all_time', { ns: COMMON_TRANSLATION });
    }

    return getSelectedDateRangeString(startDate, endDate);
  };

  const handleSelectDateRange = (selection: DateRangeSelection): void => {
    setStartDate(selection.startDate);
    setEndDate(selection.endDate);
    setSelectedDateRange(selection);
    onChangeDate?.(selection);
  };

  useEffect(() => {
    handlePillClick(selectedPill);
  }, []);

  return (
    <StyledContainer>
      <TogglePill defaultSelectedPill={selectedPill} handleTogglePillChange={handlePillClick} options={pillOptions} />
      <StyledDateContainer>
        <StyledSelectedRange data-testid="selected-date-range">
          {t('selected_date_range.label')}
          <StyledDate> {getSelectedData()} </StyledDate>
        </StyledSelectedRange>
        <StyledSelectContainer>
          <DateRangeDropdown
            value={selectedDateRange}
            dateRangeErrorTitle={t('selected_date_range.error.title')}
            dateRangeErrorDescription={t('selected_date_range.error.description', {
              dateRange:
                storeStartDate &&
                storeEndDate &&
                getSelectedDateRangeString(
                  new Date(getDateWithoutTZ(storeStartDate)),
                  new Date(getDateWithoutTZ(storeEndDate)),
                ),
            })}
            maxDate={storeEndDate ? new Date(getDateWithoutTZ(storeEndDate)) : undefined}
            minDate={storeStartDate ? new Date(getDateWithoutTZ(storeStartDate)) : undefined}
            onSelect={handleSelectDateRange}
            layout={LAYOUT_THREE}
          />
        </StyledSelectContainer>
      </StyledDateContainer>
    </StyledContainer>
  );
};

export default ChartPillAndDateFilter;
